'use client';

import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import '@splidejs/react-splide/css';
import ServiceLogoHeader from '@/components/ui/ServiceLogoHeader/ServiceLogoHeader';
import type { UserDesignItem } from '@/types';
import { buildProductOptionDisplay } from '@/utils/productOption';
import DesignPreviewImage from '../DesignPreviewImage/DesignPreviewImage';
import DesignProductSection from '../DesignProductSection/DesignProductSection';
import DesignWorkSection from '../DesignWorkSection/DesignWorkSection';
import styles from './UserDesignDetailSlider.module.scss';

export type UserDesignDetailSliderRef = {
  goPrev: () => void;
  goNext: () => void;
};

type UserDesignDetailSliderProps = {
  items: UserDesignItem[];
  initialIndex: number;
  onIndexChange?: (index: number) => void;
  /** スライドの移動開始時に true、移動終了時に false を渡す。アクションボタンの無効化に利用 */
  onTransitionChange?: (isTransitioning: boolean) => void;
  /** ドラッグ開始時に true、ドラッグ終了時に false を渡す。モーダル背景クリックの無効化に利用 */
  onDraggingChange?: (isDragging: boolean) => void;
  /** イメージ下部に表示するSP用の追加要素（アクションボタン等） */
  childrenSpActionButton?: React.ReactNode;
  /** プレビュー画像タップで拡大表示 */
  onPreviewImageClick: (imageUrl: string) => void;
};

const UserDesignDetailSlider = forwardRef<UserDesignDetailSliderRef, UserDesignDetailSliderProps>(
  function UserDesignDetailSlider(
    {
      items,
      initialIndex,
      onIndexChange,
      onTransitionChange,
      onDraggingChange,
      childrenSpActionButton,
      onPreviewImageClick,
    },
    ref,
  ) {
    const splideRef = useRef<Splide>(null);

    // 親（Modal）が持つナビボタンから前後移動できるよう、ref 経由で goPrev / goNext を公開する
    // ボタンの配置箇所がSPとPCで異なるため、親側でボタンのレイアウトを配置する
    useImperativeHandle(
      ref,
      () => ({
        goPrev: () => {
          if (splideRef.current) {
            splideRef.current.go('<');
          }
        },
        goNext: () => {
          if (splideRef.current) {
            splideRef.current.go('>');
          }
        },
      }),
      [],
    );

    const splideOptions = {
      type: 'slide',
      start: initialIndex,
      perPage: 1,
      perMove: 1,
      arrows: false,
      pagination: false,
      drag: true,
    };

    const handleMove = (index: number) => {
      onIndexChange?.(index);
    };

    const handleMoveStart = (_splide: unknown, index: number) => {
      onTransitionChange?.(true);
      handleMove(index);
    };

    const handleMoved = () => {
      onTransitionChange?.(false);
    };

    const handleDrag = () => {
      onDraggingChange?.(true);
    };

    const handleDragged = () => {
      // ドラッグ終了後、少し遅延させてからフラグを戻す
      // これがないと、マウスアップイベントと同時にフラグが戻り、背景クリックが発火してしまう
      setTimeout(() => {
        onDraggingChange?.(false);
      }, 50);
    };

    return (
      <div className={styles.sliderWrapper}>
        <Splide
          key={`user-design-slider-${initialIndex}`}
          options={splideOptions}
          ref={splideRef}
          onMove={handleMoveStart}
          onMoved={handleMoved}
          onDrag={handleDrag}
          onDragged={handleDragged}
          aria-label='デザイン詳細'
          hasTrack={false}
          className={styles.splide}
        >
          <SplideTrack className={styles.splideTrack}>
            {items.map((item) => {
              const userPrints =
                item.prints?.length > 0 ? item.prints : [{ id: 0, name: '', previewImageUrl: item.previewImageUrl }];
              const thumbnailUrl = item.workLogoURL ?? item.prints?.[0]?.previewImageUrl ?? item.previewImageUrl;
              const creatorNameList = item.creators.map((c) => c.name);
              return (
                <SplideSlide key={`slider-slide-${item.id}`}>
                  <div className={styles.slideContent}>
                    <div className={styles.headerContainer}>
                      <ServiceLogoHeader logoSize='small' justify='center' justifyPc='flex-start' />
                    </div>
                    <div className={styles.contentRow}>
                      <div className={styles.leftColumn}>
                        <DesignPreviewImage
                          userPrints={userPrints}
                          workName={item.gfWorkName}
                          onImageClick={onPreviewImageClick}
                        />
                        {/* SP版のアクションボタンを表示 */}
                        {childrenSpActionButton && (
                          <div className={styles.spActionButtonContainer}>{childrenSpActionButton}</div>
                        )}
                      </div>
                      <div className={styles.rightColumn}>
                        <DesignWorkSection
                          thumbnailUrl={thumbnailUrl}
                          workName={item.gfWorkName}
                          creatorNameList={creatorNameList}
                          workId={item.gfWorkId}
                        />
                        <DesignProductSection
                          productName={item.productName}
                          attributes={
                            buildProductOptionDisplay({
                              productType: item.productType,
                              colorName: item.productColorName,
                              sizeName: item.productSizeName,
                            }).attributes
                          }
                        />
                      </div>
                    </div>
                  </div>
                </SplideSlide>
              );
            })}
          </SplideTrack>
        </Splide>
      </div>
    );
  },
);

export default UserDesignDetailSlider;
